/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { BillingApiService } from '../../../shared/http';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LanguageService, LogService, NotifyService, StripeService } from '../../../../../core/services';
import { PortalRbacService } from '../../../shared/services/utils';
import { Router } from '@angular/router';
import { StripeProductModel, TierModel } from '../../../shared/models';
import { environment } from '../../../../../../environments/environment';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.scss']
})

export class SubscriptionComponent extends BaseComponent {
    /**
     * Form group name.
     */
    public formGroup: FormGroup;

    /**
     * View toggle.
     */
    public toggle: boolean = false;

    /**
     * Lookup intercom message object.
     */
    public intercomMessage: any = {
        general: 'I’m interested in purchasing a Portal subscription. Can I speak to sales?',
        free: 'I’m on the free tier in portal and I’m interested in upgrading. Can I speak to sales?',
        pro: 'I’m interested in purchasing the Pro Portal subscription. Can I speak to sales?',
        business: 'I’m interested in purchasing the Business Portal subscription. Can I speak to sales?',
        tailored: 'I’m interested in your Tailored Portal subscription. Can I speak to sales and organise a demo?'
    };

    /**
     * Current selected plan.
     */
    public selectedPlan: string = 'free';

    /**
     * Store the tiers for the view.
     */
    public tiers: any[] = [];

    /**
     * Products that aren't tiers
     */
    public extras: any[] = [];

    /**
     * Is stripe enabled.
     */
    public stripeDisabled: boolean = false;

    /**
     * Flag on loading state.
     */
    public isLoading: boolean = true;

    /**
     * Active portal subscription name.
     */
    public activeSubscriptionName: string = '';

    constructor(
        private billingApiService: BillingApiService,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private notifyService: NotifyService,
        private portalRbacService: PortalRbacService,
        private router: Router,
        private stripeService: StripeService,
        private translateService: LanguageService
    ) {
        super();

        const selectedTier: TierModel = this.portalRbacService.selectedTier;
        this.selectedPlan = selectedTier.tier ? selectedTier.tier : 'free';
        this.setUpForm();
        this.setUpFormSubscribers();
        this.setUpPageData();
    }

    /**
     * CTA to open intercom chat.
     */
    public contactSales(message: string): void {
        Intercom('showNewMessage', message);
    }

    /**
     * Navigate page depending on tier.
     * @param product
     */
    public linkToPage(product: TierModel): void {
        if (this.activeSubscriptionName === 'ADC Portal Pro') {
            this.logService.debug('subscriptionComponent: linkToPage', 'Downgrading from pro is changing to the Free tier.');
            // TODO Downgrading from pro is changing to the Free tier so this needs to be handled.
        }

        if (product.monthlyPriceId || product.annualPriceId) {
            const link: string = '/cm/organisation/billing/checkout/' + (this.toggle ? product.monthlyPriceId : product.annualPriceId);
            this.router.navigateByUrl(link).then().catch();
        } else {
            this.logService.warn('linkToPage', 'No tier matched');
        }
    }

    /**
     * Get connections message.
     * @param connections
     */
    public getConnectionsMessage(connections: any): string | number {
        return typeof connections === 'number' ? connections : this.translateService.translate(connections);
    }

    /**
     * backup counter message
     * @param backup
     */
    public getBackupCounterMessage(backup: any): string {
        return typeof backup === 'number'
            ? `${backup} ${this.translateService.translate('subscriptions_per_adc')}`
            : this.translateService.translate(backup);
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            toggle: [this.toggle],
        });
    }

    /**
     * Subscribe to form changes
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.get('toggle').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    this.toggle = state;
                }
            });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const products: any[] = [];
        this.stripeDisabled = environment.stripeDisabled;
        if (!this.stripeDisabled) {
            // TODO DO NOT REMOVE START
            const apiCalls: Record<string, Observable<any>> = {
                subscriptions: this.billingApiService.getSubscriptions(),
                apiProducts: this.billingApiService.getAllProducts()
            };

            forkJoin(apiCalls)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        const tierProducts: any = data.apiProducts?.filter(
                            (item: StripeProductModel): boolean => item['product_type'] === 'portal'
                        );
                        const extras: any = data.apiProducts?.filter(
                            (item: StripeProductModel): boolean => item['product_type'] === 'adc_pack'
                        );
                        this.extras = this.getFormattedExtra(extras);
                        const tiers: any[] = this.getTierOptions();
                        this.tiers = this.getFormattedTiers(tiers, tierProducts);
                        this.activeSubscriptionName = this.getActivePortalSubscriptionName(data);
                        this.isLoading = false;
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('SubscriptionComponent:setUpPageData', error.message);
                        this.tiers = this.getTierOptions();
                    }
                });
            // TODO DO NOT REMOVE END
        } else {
            this.extras = [{
                price: { annual: '$119', monthly: '$149' },
                billedAt: '$1428'
            }];
            const tiers: any[] = this.getTierOptions();
            this.tiers = this.getFormattedTiers(tiers, products);
            this.isLoading = false;
            const timerId = setTimeout((): void => {
                this.notifyService.debug('Stripe disabled');
                clearTimeout(timerId);
            }, 500);
        }
    }

    /**
     * Get formatted extra.
     * @param extras
     * @private
     */
    private getFormattedExtra(extras: StripeProductModel[]): StripeProductModel[] {
        for (const extra of extras) {
            const { annual, monthly, billedAt } = this.getProductPrices(extra);
            extra['annualPriceId'] = annual.id;
            extra['monthlyPriceId'] = monthly.id;
            extra['price'] = { annual: annual.value, monthly: monthly.value };
            extra['billedAt'] = billedAt;
        }

        return extras;
    }

    /**
     * Get formatted tiers.
     * @param tiers
     * @param products
     * @private
     */
    private getFormattedTiers(tiers: any, products: StripeProductModel[]): any[] {
        for (const tier of tiers) {
            const matchedProduct: StripeProductModel = products.find((item: any): boolean => item.stripe_product_id === tier.id);
            if (!this.stripeDisabled && matchedProduct) {
                const tierTitles: any[] = tiers.map((item: any): any => item.plan);
                const selectedIndex: number = tierTitles.indexOf(this.selectedPlan);
                const tierIndex: number = tierTitles.indexOf(tier.plan);
                const upGradeDownGradeMessage: 'upgrade' | 'downgrade' = selectedIndex < tierIndex ? 'upgrade' : 'downgrade';
                const { annual, monthly, billedAt } = this.getProductPrices(matchedProduct);
                tier.priceDetails.price = billedAt;
                tier.annualPriceId = annual.id;
                tier.monthlyPriceId = monthly.id;
                tier.linkText = this.selectedPlan === tier.plan ? 'subscriptions_current_plan' : upGradeDownGradeMessage;

            } else {
                this.logService.debug('SubscriptionComponent:getFormattedTiers', 'No matching tier: ' + tier.plan);
            }
        }

        return tiers;
    }

    /**
     * Get product prices
     * @param matchedProduct
     * @private
     */
    private getProductPrices(matchedProduct: StripeProductModel): any {
        const prices: any = matchedProduct.prices;
        if (!prices) {
            this.logService.error('Subscription:getProductPrices', 'No prices found in products object.');

            return {
                annual: { id: '', value: '$0' },
                monthly: { id: '', value: '$0' },
                billedAt: '$0'
            };
        }

        const annual: any = prices.find((item: any): boolean => item.interval === 'year');
        const month: any = prices.find((item: any): boolean => item.interval === 'month');
        // Some products don't have annual prices.
        const altValues: any = annual ? annual : month;

        return {
            annual: {
                id: altValues.id,
                value: this.stripeService.getCurrencySymbol(altValues.currency) + this.stripeService.getUnitPrice(altValues.unitAmount / 12)
            },
            monthly: {
                id: month.id,
                value: this.stripeService.getCurrencySymbol(month.currency) + this.stripeService.getUnitPrice(month.unitAmount)
            },
            billedAt: this.stripeService.getCurrencySymbol(altValues.currency) + this.stripeService.getUnitPrice(altValues.unitAmount)
        };
    }

    /**
     * Get active subscription name. e.g. ADC Portal Business or ADC Portal Pro
     * @param data
     * @private
     */
    private getActivePortalSubscriptionName(data: any): any {
        const activeSubscriptions: any[] = data.subscriptions?.filter((item: any): boolean => item.status === 'active');
        if (!activeSubscriptions.length) {
            this.logService.warn('SubscriptionComponent:getActivePortalSubscriptionName', 'No active subscription found');
        } else {
            let productSubscriptions: any[] = [];

            for (const subscription of activeSubscriptions) {
                const productIds: any[] = subscription.items.map((item: any): any => item.stripeProductID);
                const matchedProducts: any[] = data?.apiProducts.filter(
                    (product: any): boolean => productIds?.includes(product.stripe_product_id)
                );
                productSubscriptions = productSubscriptions.concat(matchedProducts);
            }

            const portalSubscription: any = productSubscriptions.find((item: any): boolean => item.product_type === 'portal');

            return portalSubscription?.name;
        }
    }

    /**
     * Get tier options.
     */
    private getTierOptions(): any[] {
        const gridDetails: any = this.gridObjects();

        return this.formatTierOptions(gridDetails);
    }

    /**
     * Format tier options.
     * @param gridDetails
     * @private
     */
    private formatTierOptions(gridDetails: any): any[] {
        for (const tier of gridDetails) {
            const options: any = this.portalRbacService.tierOptions(tier.plan);
            const { portalList, accessList, security } = this.sortTierOptions(options);
            tier.portalList = portalList;
            tier.accessList = accessList;
            tier.security = security;
        }

        return gridDetails;
    }

    /**
     * Sort tier options into arrays for the checklists
     * @param options
     * @private
     */
    private sortTierOptions(options: any): any {
        const portalList: any = [];
        const accessList: any = [];
        const security: any = [];
        for (const option in options) {
            if (options.hasOwnProperty(option) && options[option] === true) {
                switch (option) {
                    case 'dashboard':
                    case 'adc_backups':
                    case 'online_updates':
                    case 'asset_scheduling':
                    case 'dedicated_success_manager':
                    case 'professional_services':
                        portalList.push(option);
                        break;
                    case 'secure_direct_access':
                    case 'secure_warp_remote_access':
                        accessList.push(option);
                        break;
                    case 'adc_security_insights':
                    case 'adc_certificate_insights':
                    case 'single_sign_on':
                    case 'single_sign_on_tailored':
                    case '2fa':
                    case 'audit_log':
                        security.push(option);
                        break;
                }
            }
        }

        return { portalList, accessList, security };
    }

    /**
     * Grid objects
     * @private
     */
    private gridObjects(): any {
        return [
            {
                id: 'prod_RqnZsTYmuFHTXr',
                plan: 'free',
                title: 'subscriptions_free',
                subTitle: 'subscriptions_for_starter_projects',
                price: { annual: '$0', monthly: '$0' },
                priceDetails: { title: 'subscriptions_billed_at', price: '$0', term: '/yr' },
                linkText: 'downgrade',
                intercomMessage: this.intercomMessage.free,
                connections: 6,
                backups: 2,
                history: 'subscriptions_backup_history',
                supportLevel: 'subscriptions_support_type',
                supportMessage: '',
                portalList: [],
                accessList: [],
                security: [],
                comingSoon: ['license_management', 'api_access']
            },
            {
                id: 'prod_RqnaPfUxle9uh3',
                plan: 'professional',
                title: 'subscriptions_pro',
                subTitle: 'subscriptions_pro_subtitle',
                price: { annual: '$99', monthly: '$130' },
                priceDetails: { title: 'subscriptions_billed_at', price: '$1188', term: '/yr' },
                intercomMessage: this.intercomMessage.pro,
                connections: 12,
                backups: 25,
                history: 'subscriptions_backup_history',
                supportLevel: '9am - 5pm',
                supportMessage: 'subscriptions_support_message',
                portalList: [],
                accessList: [],
                security: [],
                comingSoon: ['license_management', 'api_access']
            },
            {
                id: 'prod_RqnczfveFd3kgE',
                plan: 'business',
                title: 'subscriptions_business',
                subTitle: 'subscriptions_business_subtitle',
                price: { annual: '$499', monthly: '$630' },
                priceDetails: { title: 'subscriptions_billed_at', price: '$5988', term: '/yr' },
                intercomMessage: this.intercomMessage.pro,
                connections: 30,
                backups: 'unlimited',
                history: 'subscriptions_backup_history',
                supportLevel: '24/7',
                supportMessage: 'subscriptions_support_message',
                portalList: [],
                accessList: [],
                security: [],
                comingSoon: ['license_management', 'api_access', 'asset_scheduling_pipelines']
            },
            {
                id: 'tailored',
                plan: 'tailored',
                title: 'subscriptions_tailored',
                subTitle: 'subscriptions_tailored_subtitle',
                price: { annual: 'Let\'s talk', monthly: 'Let\'s talk' },
                intercomMessage: this.intercomMessage.tailored,
                connections: 'unlimited',
                backups: 'unlimited',
                history: 'subscriptions_backup_history',
                supportLevel: '24/7',
                supportMessage: 'subscriptions_support_message',
                portalList: [],
                accessList: [],
                security: [],
                comingSoon: ['license_management', 'api_access', 'asset_scheduling_pipelines', 'white_label']
            }
        ];
    }
}
